import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../context/user";
import {
  ensureFreshAccessToken,
  clearAuthTokens,
} from "../api/axiosInstance";
import { getUserByIdApi } from "../api/UserApi";
import {
  decodeAuthToken,
  getAuthRoles,
  isAccessTokenExpired,
} from "../utils/authToken";
import {
  getAddressGateComplete,
  setAddressGateComplete,
  subscribeAddressGate,
} from "../utils/addressGate";
import LoadingSpinner from "../Components/Common/LoadingSpinner";

const ADDRESS_INCOMPLETE_TOAST_ID = "address-incomplete-gate";
const ADDRESS_INCOMPLETE_MESSAGE =
  "Panele devam edebilmek için profilinizdeki adres bilgilerini eksiksiz kaydetmeniz gerekiyor.";

const AuthProtected = (props) => {
  const { role, children } = props;
  const [state, dispatch] = useContext(UserContext);
  const location = useLocation();
  const [bootstrapping, setBootstrapping] = useState(true);
  const [sessionOk, setSessionOk] = useState(false);
  const [addressCheckLoading, setAddressCheckLoading] = useState(true);
  const [addressComplete, setAddressComplete] = useState(
    () => getAddressGateComplete()
  );

  useEffect(() => {
    return subscribeAddressGate((value) => {
      setAddressComplete(value);
      if (value !== null) {
        setAddressCheckLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const accessToken = localStorage.getItem("authToken");
      const refreshToken = localStorage.getItem("refreshToken");

      if (!accessToken && !refreshToken) {
        if (!cancelled) {
          setSessionOk(false);
          setBootstrapping(false);
        }
        return;
      }

      try {
        if (!accessToken || isAccessTokenExpired(accessToken)) {
          await ensureFreshAccessToken();
        }

        if (!cancelled) {
          dispatch({ type: "HYDRATE_FROM_TOKEN" });
          setSessionOk(true);
        }
      } catch {
        clearAuthTokens();
        if (!cancelled) {
          dispatch({ type: "LOGOUT" });
          setSessionOk(false);
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useLayoutEffect(() => {
    if (!sessionOk) return;

    const token = localStorage.getItem("authToken");
    const decoded = decodeAuthToken(token);
    const tokenRole = state.role ?? decoded?.role;
    const userRoles = getAuthRoles(tokenRole);

    if (!userRoles.includes("Öğrenci")) {
      setAddressCheckLoading(false);
      setAddressComplete(true);
      setAddressGateComplete(true);
      return;
    }

    const cached = getAddressGateComplete();
    if (cached === null) {
      setAddressCheckLoading(true);
    }
  }, [sessionOk, state.role, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const checkAddressCompletion = async () => {
      if (!sessionOk) return;

      const token = localStorage.getItem("authToken");
      const decoded = decodeAuthToken(token);
      const tokenRole = state.role ?? decoded?.role;
      const userRoles = getAuthRoles(tokenRole);
      const isStudent = userRoles.includes("Öğrenci");

      if (!isStudent) {
        setAddressComplete(true);
        setAddressGateComplete(true);
        setAddressCheckLoading(false);
        return;
      }

      try {
        setAddressCheckLoading(true);
        const userId = state.userId ?? decoded?.userId;
        const response = await getUserByIdApi(userId, { skipErrorToast: true });
        if (!cancelled) {
          const complete = Boolean(response.data?.isAddressComplete);
          setAddressComplete(complete);
          setAddressGateComplete(complete);
        }
      } catch {
        if (!cancelled) {
          const cached = getAddressGateComplete();
          if (cached !== null) {
            setAddressComplete(cached);
          } else {
            setAddressComplete(true);
            setAddressGateComplete(true);
          }
        }
      } finally {
        if (!cancelled) {
          setAddressCheckLoading(false);
        }
      }
    };

    checkAddressCompletion();
    return () => {
      cancelled = true;
    };
  }, [sessionOk, state.role, state.userId, location.pathname]);

  useEffect(() => {
    if (
      bootstrapping ||
      !sessionOk ||
      addressCheckLoading ||
      addressComplete !== false
    ) {
      return;
    }

    const tokenRole =
      state.role ?? decodeAuthToken(localStorage.getItem("authToken"))?.role;
    const userRoles = getAuthRoles(tokenRole);

    if (
      userRoles.includes("Öğrenci") &&
      location.pathname !== "/panel/profil"
    ) {
      toast.warning(ADDRESS_INCOMPLETE_MESSAGE, {
        toastId: ADDRESS_INCOMPLETE_TOAST_ID,
        autoClose: 5000,
        position: "top-center",
      });
    }
  }, [
    bootstrapping,
    sessionOk,
    addressCheckLoading,
    addressComplete,
    location.pathname,
    state.role,
  ]);

  if (bootstrapping) {
    return <LoadingSpinner tip="Oturum kontrol ediliyor…" />;
  }

  if (!sessionOk) {
    return <Navigate to="/giris" replace />;
  }

  if (addressCheckLoading) {
    return <LoadingSpinner tip="Profil bilgileri kontrol ediliyor…" />;
  }

  const tokenRole =
    state.role ?? decodeAuthToken(localStorage.getItem("authToken"))?.role;
  const userRoles = getAuthRoles(tokenRole);
  const allowed =
    !role?.length || userRoles.some((userRole) => role.includes(userRole));

  if (!allowed) {
    return <Navigate to="/panel/anasayfa" replace />;
  }

  if (
    userRoles.includes("Öğrenci") &&
    addressComplete === false &&
    location.pathname !== "/panel/profil"
  ) {
    return <Navigate to="/panel/profil" replace />;
  }

  return <>{children}</>;
};

export { AuthProtected };
